import React, { useState } from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Users, Shield, Eye, Accessibility, MessageCircle, Award, ChevronDown, ChevronUp, Scale, RefreshCw } from 'lucide-react';
export function CoProduction() {
  const [expandedPrinciple, setExpandedPrinciple] = useState<number | null>(null);
  const principles = [{
    icon: <Heart className="h-10 w-10" data-id="element-1091" />,
    letter: 'a',
    title: 'Lived Experience as Expertise',
    summary: 'We recognise lived experience as specialist knowledge, equal to academic or professional expertise.',
    details: ['Those with lived experience will be involved from the earliest stages of project design through to evaluation.', 'We commit to ensuring their insights shape content, methods, and priorities — not merely confirm pre-made decisions.']
  }, {
    icon: <Scale className="h-10 w-10" data-id="element-1092" />,
    letter: 'b',
    title: 'Equity and Fair Compensation',
    summary: 'Co-production involves both creative and emotional labour. Fair recognition of this work is a matter of justice.',
    details: ['Wherever possible, co-producers will receive fair monetary payment for their work, reflecting the expertise and labour involved.', 'As a social justice organisation with limited funding, there may be occasions where financial payment is not possible. In such cases, we will collaborate through voluntary or in-kind exchange.', "When this happens, we will offer clear, meaningful alternatives (such as training, mentoring, shared authorship, access to paid courses), or other agreed benefits that support the contributor's growth and autonomy.", 'Such arrangements will be mutually negotiated, based on informed consent and fairness — not assumption or expectation of unpaid work.', 'We remain committed to building financial sustainability so that monetary payment becomes the norm for lived experience contributions.']
  }, {
    icon: <Shield className="h-10 w-10" data-id="element-1093" />,
    letter: 'c',
    title: 'Consent and Boundaries',
    summary: 'Participation is voluntary, informed, and reversible at any stage.',
    details: ['Co-producers retain agency over how their stories, identities, and experiences are represented.', 'Sensitive or personal disclosures will never be used without explicit consent and the option to withdraw later.', "While all efforts will be made to remove a participant's contributions when requested, in some cases (such as when the contribution is already public) it will not be possible."]
  }, {
    icon: <Eye className="h-10 w-10" data-id="element-1094" />,
    letter: 'd',
    title: 'Shared Power and Transparency',
    summary: 'Decision-making processes will be clearly communicated and power dynamics made visible.',
    details: ['Co-producers will be involved in setting agendas, reviewing outputs, and determining how success is measured.', 'We recognise the different kinds of power people hold (institutional, financial, social) and strive to make those dynamics visible and accountable.']
  }, {
    icon: <Accessibility className="h-10 w-10" data-id="element-1095" />,
    letter: 'e',
    title: 'Accessibility and Care',
    summary: 'Co-production spaces must be accessible to diverse body sizes, disabilities, learning styles, and sensory needs.',
    details: ['Meetings will include rest breaks, captioning or transcripts, and flexible formats.', 'Emotional wellbeing and mutual respect are prioritised.', 'We approach collaboration as a caring relationship, not an extractive process.']
  }, {
    icon: <MessageCircle className="h-10 w-10" data-id="element-1096" />,
    letter: 'f',
    title: 'Community Accountability',
    summary: 'Co-production is an ongoing relationship with community, not a one-off consultation.',
    details: ['Feedback from learners, facilitators, and partners will directly inform future practice.', 'If harm occurs, we will address it transparently and with care, using restorative approaches where possible.']
  }, {
    icon: <Award className="h-10 w-10" data-id="element-1097" />,
    letter: 'g',
    title: 'Recognition and Credit',
    summary: 'Co-producers will be credited for their contributions in all relevant materials.',
    details: ['Credits will appear in courses, publications, websites, and events — unless the co-producer chooses anonymity.', 'Intellectual and creative ownership will be respected. ReGrowth will seek consent for any future reuse or adaptation of co-produced materials.']
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1098">
      <HeroSection title="Co-Production Principles" subtitle="How we build learning experiences with — not for — our community." tag="About ReGrowth" bgColor="lime" textColor="navy" data-id="element-1099" />

      {/* Purpose */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1100">
        <h2 className="text-3xl font-black uppercase mb-8 text-brand-black border-b-4 border-brand-lime inline-block pb-2" data-id="element-1101">
          Purpose
        </h2>
        <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-1102">
          <p data-id="element-1103">
            ReGrowth exists to create learning that is rooted in{' '}
            <strong className="text-brand-lime" data-id="element-1104">lived experience</strong>,{' '}
            <strong className="text-brand-lime" data-id="element-1105">collective care</strong>, and{' '}
            <strong className="text-brand-lime" data-id="element-1106">social change</strong>.
          </p>
          <p data-id="element-1107">
            Co-production means that people with direct, lived experience of the
            topics we teach — particularly those affected by fat oppression and
            intersecting injustices — shape, design, deliver, and evaluate our
            work alongside us.
          </p>
          <div className="bg-brand-lime/10 border-l-4 border-brand-lime p-6" data-id="element-1108">
            <p className="font-bold text-brand-navy" data-id="element-1109">
              We reject extractive or tokenistic consultation. Co-production at
              ReGrowth is about shared power, shared credit, and shared
              learning.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1110">
        <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-1111">
          Core Principles
        </h2>
        <p className="text-gray-600 mb-12" data-id="element-1112">
          Click any principle to read the full detail.
        </p>

        <div className="space-y-4" data-id="element-1113">
          {principles.map((principle, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="border-2 border-brand-black bg-white" data-id="element-1114">
              <button onClick={() => setExpandedPrinciple(expandedPrinciple === index ? null : index)} className="w-full p-6 flex items-start gap-6 text-left hover:bg-gray-50 transition-colors" data-id="element-1115">
                <div className="text-brand-lime flex-shrink-0 mt-1" data-id="element-1116">
                  {principle.icon}
                </div>
                <div className="flex-grow" data-id="element-1117">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1118">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-lime bg-brand-lime/10 px-2 py-1" data-id="element-1119">
                      {principle.letter}
                    </span>
                    <h3 className="text-xl font-black uppercase text-brand-black" data-id="element-1120">
                      {principle.title}
                    </h3>
                  </div>
                  <p className="text-gray-700" data-id="element-1121">{principle.summary}</p>
                </div>
                <div className="flex-shrink-0 mt-1" data-id="element-1122">
                  {expandedPrinciple === index ? <ChevronUp className="h-6 w-6 text-brand-lime" data-id="element-1123" /> : <ChevronDown className="h-6 w-6 text-gray-400" data-id="element-1124" />}
                </div>
              </button>

              <AnimatePresence data-id="element-1125">
                {expandedPrinciple === index && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-1126">
                    <div className="px-6 pb-6 pt-2 ml-16 border-t-2 border-gray-100" data-id="element-1127">
                      <div className="space-y-4" data-id="element-1128">
                        {principle.details.map((detail, i) => <div key={i} className="flex items-start" data-id="element-1129">
                            <span className="text-brand-lime mr-3 mt-1 flex-shrink-0" data-id="element-1130">
                              •
                            </span>
                            <p className="text-gray-700 leading-relaxed" data-id="element-1131">
                              {detail}
                            </p>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </section>

      {/* Implementation */}
      <section className="py-16 bg-white" data-id="element-1132">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1133">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1134">
            Implementation
          </h2>
          <div className="border-2 border-brand-black p-8 bg-brand-lime/5" data-id="element-1135">
            <div className="flex items-start gap-4" data-id="element-1136">
              <RefreshCw className="h-8 w-8 text-brand-lime flex-shrink-0 mt-1" data-id="element-1137" />
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-1138">
                Co-founders will review co-production practices{' '}
                <strong data-id="element-1139">annually</strong> to ensure equity, transparency, and
                compliance with the Equality Act 2010 and UK GDPR.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Accountability and Growth */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-1140">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1141">
          <h2 className="text-3xl font-black uppercase mb-8" data-id="element-1142">
            Accountability & Growth
          </h2>
          <p className="text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-1143">
            Co-production at ReGrowth is a practice of mutual growth. We welcome
            feedback and critique from our community and commit to revising our
            principles and processes in response.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left mt-12" data-id="element-1144">
            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1145">
              <h3 className="font-bold text-lg text-brand-lime mb-2" data-id="element-1146">Listen</h3>
              <p className="text-sm text-gray-200" data-id="element-1147">
                We actively seek feedback from co-producers, learners, and
                community members.
              </p>
            </div>
            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1148">
              <h3 className="font-bold text-lg text-brand-lime mb-2" data-id="element-1149">
                Reflect
              </h3>
              <p className="text-sm text-gray-200" data-id="element-1150">
                We review our practices annually and when harm is identified.
              </p>
            </div>
            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1151">
              <h3 className="font-bold text-lg text-brand-lime mb-2" data-id="element-1152">Revise</h3>
              <p className="text-sm text-gray-200" data-id="element-1153">
                We update our principles and processes based on what we learn.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Summary Statement */}
      <section className="py-24 bg-brand-lime" data-id="element-1154">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1155">
          <Users className="h-16 w-16 text-brand-navy mx-auto mb-8" data-id="element-1156" />
          <h2 className="text-3xl md:text-5xl font-black uppercase mb-8 text-brand-navy leading-tight" data-id="element-1157">
            Co-production at ReGrowth means working{' '}
            <em className="not-italic underline decoration-4 underline-offset-4" data-id="element-1158">
              with
            </em>
            , not for.
          </h2>
          <p className="text-xl text-brand-navy/80 leading-relaxed max-w-3xl mx-auto" data-id="element-1159">
            We honour lived experience as expertise, compensate it fairly, share
            power openly, and centre care, access, and accountability in
            everything we create.
          </p>
        </div>
      </section>
    </div>;
}